import React, { useState, useEffect, useMemo } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { FiEdit2, FiEye, FiPlus } from 'react-icons/fi';
import '../pagination/pagination.css';
import Pagination from '../pagination/pagination';
import LeaseApiService from '../../services/apiService';
import RangeChips from '../shared/rangeChips';
import SummaryStrip, { countBy, totalFrom } from '../shared/summaryStrip';
import { useRange, fetchRanged, useCardFilter, valueTiles } from '../shared/rangedList';
import PmUnitDetailForm from './pmUnitDetailForm';
import '../customerMaster/customerListing.css';

// Same data and endpoints as Unit Master RA (Lease Finance unitmanagementra API).
// List of all units (every unit class) with an Add button that opens the PM Unit Details popup.
const PmUnitDetailListing = () => {
  const apiService = useMemo(() => new LeaseApiService(), []);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState();
  const [showForm, setShowForm] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const { range, setRange, cache: rangeCache } = useRange(() => getItems(1));
  const cf = useCardFilter(rowData, range, rangeCache);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('RoleAccess')) || [];
    setPermissions(stored.find((acc) => acc.formName === 'PM Unit Details') || null);
  }, []);

  useEffect(() => {
    getItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const getItems = async (newPageNo = pagination?.currentPage) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('PageNo', newPageNo || 1);
      params.append('PageSize', pageSize || 20);
      params.append('PropertyNo', '');
      params.append('UnitNo', '');
      params.append('UnitClass', '');
      params.append('UnitType', '');
      params.append('UnitCategory', '');
      params.append('CreatedBy', '');
      params.append('UnitModel', '');
      const res = await fetchRanged({ apiService, endpoint: 'GET_UNIT_RA_LIST', params, range, cache: rangeCache, pageNo: newPageNo, pageSize });
      setRowData(res?.data?.result || []);
      // The Lease Finance list doesn't always return every paginationData field the shared
      // Pagination needs, so derive the gaps (same as the Unit Master RA listing).
      const pd = res?.data?.paginationData || {};
      const list = res?.data?.result || [];
      const size = Number(pd.pageSize ?? pd.PageSize) || pageSize || 20;
      const totalRecords = Number(pd.totalRecords ?? pd.TotalRecords ?? pd.totalCount ?? pd.TotalCount) || list.length;
      setPagination({
        ...pd,
        currentPage: Number(pd.currentPage ?? pd.CurrentPage ?? pd.pageNo ?? pd.PageNo) || newPageNo || 1,
        totalRecords,
        totalPages: Number(pd.totalPages ?? pd.TotalPages) || Math.max(1, Math.ceil(totalRecords / size)),
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setSelectedRow(null);
    setViewOnly(false);
    setShowForm(true);
  };
  const openEdit = (row) => {
    if (permissions?.canUpdate !== true) return;
    setSelectedRow(row);
    setViewOnly(false);
    setShowForm(true);
  };
  const openView = (row) => {
    setSelectedRow(row);
    setViewOnly(true);
    setShowForm(true);
  };

  const columnDefs = [
    {
      headerName: 'Actions',
      field: 'ref_No',
      colId: 'actions',
      pinned: 'left',
      width: 110,
      sortable: false,
      filter: false,
      suppressMovable: true,
      cellRenderer: (params) => (
        <div className="cl-actions">
          <button type="button" className="cl-ico" title="View" aria-label="View" onClick={() => openView(params.data)}><FiEye size={18} /></button>
          <button type="button" className="cl-ico" title="Edit" aria-label="Edit" disabled={permissions?.canUpdate !== true} onClick={() => openEdit(params.data)}><FiEdit2 size={18} /></button>
        </div>
      ),
    },
    { headerName: 'Ref no', field: 'ref_No', filter: true, floatingFilter: true, suppressMovable: true, width: 150, pinned: 'left', cellClass: 'cl-id-cell' },
    { headerName: 'Property Name', field: 'property_Name', filter: true, floatingFilter: true, suppressMovable: true, width: 220 },
    { headerName: 'Unit No', field: 'unit_No', filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
    { headerName: 'Unit Class', field: 'unit_Class', filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
    { headerName: 'Unit Type', field: 'unit_Type', filter: true, floatingFilter: true, suppressMovable: true, width: 140 },
    { headerName: 'Status', field: 'status', filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
    { headerName: 'Rent / Annum', field: 'rent_Per_Annum', filter: true, floatingFilter: true, suppressMovable: true, width: 140 },
    { headerName: 'Property No', field: 'property_No', filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
  ];

  return (
    <div className="pm-unit-page cl-page">
      <div className="cl-titlebar">
        <div className="cl-title">
          <h1>PM Unit Details</h1>
          <p>Record the owner&apos;s bank and IBAN details for each unit.</p>
        </div>
        <RangeChips value={range} onChange={setRange} />
      </div>
      <SummaryStrip
        label="Total units"
        total={totalFrom(pagination, rowData.length)}
        subtitle="All properties"
        heroNote={cf.inPeriod ? 'Chosen period' : 'All records'} heroActive={!cf.filter} onHero={() => cf.setFilter(null)}
        tiles={[
          { label: cf.inPeriod ? 'In period' : 'On this page', value: cf.statsRows.length, note: 'units', active: !cf.filter, onClick: () => cf.setFilter(null) },
          ...valueTiles(cf, 'unit_Class', 3),
          ...valueTiles(cf, 'status', 2),
        ]}
        ariaLabel="PM unit summary"
      />
      <section className="cl-panel cl-list">
        <div className="cl-list-head">
          <div>
            <h2>PM Unit Details</h2>
            <div className="cl-sub">Use the eye to view a unit, the pencil to edit its bank details.</div>
          </div>
          {permissions?.canAdd === true && (
            <button type="button" className="cl-btn cl-btn-primary" onClick={openNew}><FiPlus size={16} />Add</button>
          )}
        </div>
        <div className="cl-grid-wrap">
          <div className="ag-theme-quartz customer-grid" style={{ height: 650 }}>
            <AgGridReact
              rowData={cf.filter ? cf.shownRows : rowData}
              columnDefs={columnDefs}
              loading={loading}
              enableFilter={true}
              floatingFilter={true}
              onRowDoubleClicked={(e) => openEdit(e.data)}
            />
          </div>
          {!cf.filter && <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={getItems} />}
        </div>
      </section>
      <PmUnitDetailForm show={showForm} initialRow={selectedRow} viewOnly={viewOnly} onHide={() => setShowForm(false)} onSaved={() => { rangeCache.current.key = ''; getItems(pagination?.currentPage || 1); }} />
    </div>
  );
};

export default PmUnitDetailListing;
